import { useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import Form from 'react-bootstrap/Form';
import { useNavigate } from 'react-router-dom';
import { DetailSection } from '../../../../../../../components/generic';
import { FormLabel, FormRequiredLegend } from '../../../../../../../components/forms';
import { useToast } from '../../../../../../../context';
import { useCreateReceiptMutation } from '../../../../../../../services/endpoints/finance/receipt';
import { Course } from '../../../../../../../types/course.type';
import { getErrorMessage } from '../../../../../../../utils/utils';
import { NumberInput } from '../../../../../../../components/inputs/NumberInput';

interface Props {
  course: Course | undefined;
  /** Prefix controlIds to avoid duplicate HTML id conflicts when rendered in both desktop and mobile */
  idPrefix?: string;
}

export const SessionReceiptsSection = ({ course, idPrefix = '' }: Props) => {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [createReceipt] = useCreateReceiptMutation();
  const fileRef = useRef<HTMLInputElement | null>(null);

  const [storeName, setStoreName] = useState<string>();
  const [dateOfPurchase, setDateOfPurchase] = useState<string>();
  const [totalReceipt, setTotalReceipt] = useState<number>();
  const [notesReceipt, setNotesReceipt] = useState<string>('');
  const [file, setFile] = useState<File>();
  const [fileError, setFileError] = useState<string | null>(null);

  const [storeNameTouched, setStoreNameTouched] = useState(false);
  const [dateOfPurchaseTouched, setDateOfPurchaseTouched] = useState(false);
  const [totalReceiptTouched, setTotalReceiptTouched] = useState(false);

  const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selected = event.target.files?.[0];
    if (!selected) return;

    if (!allowedTypes.includes(selected.type)) {
      setFileError('Only image files (JPG, PNG, GIF, WEBP) are allowed.');
      event.target.value = '';
      setFile(undefined);
      return;
    }

    setFileError(null);
    setFile(selected);
  };

  const handleSubmit = async () => {
    try {
      const res = await createReceipt({
        store_name: storeName ?? '',
        total: totalReceipt ?? 0,
        course_id: course?.id ?? 0,
        purchased_at: dateOfPurchase,
        image: file
      }).unwrap();

      if (res && res.receipt) {
        navigate(`/admin/finance/receipts/${res.receipt.id}`);
        addToast('Receipt created successfully', 'success');
      } else {
        addToast(getErrorMessage(res), 'error');
      }
    } catch (e) {
      addToast(getErrorMessage(e), 'error');
    }
  };

  const isSubmitDisabled =
      !storeName ||
      storeName.trim() === '' ||
      !dateOfPurchase ||
      dateOfPurchase.trim() === '' ||
      !totalReceipt ||
      totalReceipt <= 0;

  return (
      <DetailSection title="SFC Corporate Credit Card Receipts">
        {!course?.receipts?.length ? (
            <p>No receipts have been uploaded to this session.</p>
        ) : (
            <ul>
              {course.receipts.map((receipt, index) => (
                  <li key={index}>
                    <span> · </span>
                    {` ${new Date(receipt?.purchased_at).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: '2-digit',
                      day: '2-digit'
                    })} `}
                    <button
                        type="button"
                        className="btn btn-link p-0"
                        onClick={() => navigate(`/admin/finance/receipts/${receipt?.id}`)}
                    >
                      {` ${receipt?.store_name} [$${receipt?.total}]`}
                    </button>
                  </li>
              ))}
            </ul>
        )}

        <FormRequiredLegend />
        <Form>
          <Form.Group
              className="mb-3"
              controlId={`${idPrefix}storeName`}
          >
            <FormLabel required>Store Name</FormLabel>
            <Form.Control
                type="text"
                required
                value={storeName}
                onChange={(e) => setStoreName(e.target.value)}
                onBlur={() => setStoreNameTouched(true)}
                isInvalid={storeNameTouched && (!storeName || storeName.trim() === '')}
                aria-required
            />
            <Form.Control.Feedback type="invalid">Store name is required.</Form.Control.Feedback>
          </Form.Group>
        </Form>

        <Form>
          <Form.Group
              className="mb-3"
              controlId={`${idPrefix}dateOfPurchase`}
          >
            <FormLabel required>Date of Purchase</FormLabel>
            <Form.Control
                type="date"
                required
                value={dateOfPurchase}
                onChange={(e) => setDateOfPurchase(e.target.value)}
                onBlur={() => setDateOfPurchaseTouched(true)}
                isInvalid={dateOfPurchaseTouched && (!dateOfPurchase || dateOfPurchase.trim() === '')}
                aria-required
            />
            <Form.Control.Feedback type="invalid">
              Date of purchase is required.
            </Form.Control.Feedback>
          </Form.Group>
        </Form>

        <Form>
          <Form.Group
              className="mb-3"
              controlId={`${idPrefix}totalReceipt`}
          >
            <FormLabel required>Total</FormLabel>
            <NumberInput
                required
                min="0"
                value={totalReceipt}
                onChange={(e) => setTotalReceipt(Number(e.target.value))}
                onBlur={() => setTotalReceiptTouched(true)}
                isInvalid={totalReceiptTouched && (!totalReceipt || totalReceipt <= 0)}
                aria-required
            />
            <Form.Control.Feedback type="invalid">
              Total amount is required and must be greater than 0.
            </Form.Control.Feedback>
          </Form.Group>
        </Form>

        <Form>
          <>
            <FormLabel>Notes</FormLabel>
            <Form.Control
                as="textarea"
                rows={3}
                value={notesReceipt}
                onChange={(e) => setNotesReceipt(e.target.value)}
            />
            <Form.Text className="text-muted">
              Please include detailed notes here explaining the source of cost and need for
              reimbursement.
            </Form.Text>
          </>
          <Form.Group
              className="mb-lg-3 mb-1"
              controlId={`${idPrefix}receiptImage`}
          >
            <FormLabel>Upload Image</FormLabel>
            <Form.Control
                name="image"
                ref={fileRef}
                className="form-control"
                accept="image/*"
                type="file"
                onChange={handleFileChange}
            />
            {fileError && (
                <div className="text-danger mt-1" style={{ fontSize: '0.875rem' }}>
                  {fileError}
                </div>
            )}
          </Form.Group>
        </Form>

        <Button
            onClick={handleSubmit}
            disabled={isSubmitDisabled}
        >
          Create Receipt
        </Button>
      </DetailSection>
  );
};